import {
  WORLD_SESSION_TIMES_MAX,
  WorldRoomEntry,
  WorldSessionChildrenPage,
  WorldSessionTimes,
  type WorldSessionProjection,
} from "@cleo/contracts";
import {
  describeTidyPlan,
  planSessionTidy,
  RetiredSessions,
  SessionTidyRuns,
  validTidyDays,
  type SessionTidyDependencies,
  type TidyPocketSession,
  type TidyRequest,
  type TidyRetirementOutcome,
  type TidyWorldSession,
} from "@cleo/shared/sessionTidy";
import type { ConvexClient } from "convex/browser";
import { makeFunctionReference } from "convex/server";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";

import { personRoomPageAsIndexPage, refusalOfWorldError } from "./webHostWorldDoors";

/**
 * Can 80 in the browser host (W-4568777b): the rail's Tidy up, answered from the World itself.
 *
 * The Desk's rail posts its tidy to one Mac route, /api/neucleos/world/sessions/tidy (apps/server sessionTidyHttp.ts),
 * which runs the ONE tidy (@cleo/shared/sessionTidy: the planner and runner Cleo's plan_session_tidy and
 * archive_quiet_sessions use too) over the Desk's conversations and the World read under its grant. The browser host has
 * no Mac server and no grant, only the window's own sign-in; this is the same tidy over the World's person doors, in the
 * Desk route's exact shapes, so the dialog (SessionTidyDialog.tsx), its client (sessionTidyClient.ts) and the rail's
 * running tidy (useSessionTidyRun.ts) are the same code on both hosts.
 *
 *   mode "preview"  → { ok, plan, words }   planSessionTidy + describeTidyPlan
 *   mode "archive"  → { ok, run }           SessionTidyRuns.join: the rail asks again with the same operation until done
 *   mode "changes" | "pins"  → null (the Mac's: when this Desk last archived, and the pins Cleo's Desk tidy keeps)
 *
 * The browser holds no conversation of its own (the Desk's projection_threads), so every candidate is a session the
 * World says this person opened, judged by the World's own time for it, as the Desk judges the sessions it holds no
 * conversation for (sessionTidyLive.ts readWorldSessions, readPocketSessions): the person's Pocket room through
 * boards:readSessionChildren, every other room through the directory (boards:readRooms), each session's time and
 * retirement through boards:readSessionTimes, each candidate confirmed by its one entry (boards:readRooms {entry}), and
 * the archive itself through the person's own retirement door (worldSessionRetirementRail:retireOwnSession, W-9a3e7442),
 * one session at a time: that door is one World action with the reservation and the retirement inside, and the World
 * refuses a move prepared at a cut its head has left (apps/server worldMoveQueue.ts), so the browser sends one move at a
 * time as the Desk's queue does.
 */

const WORKROOM_WIRE = "workroom-v1" as const;
const ROOM_ID = /^R_[A-Za-z0-9._:-]{1,253}$/u;
const SESSION_ID = /^S_[A-Za-z0-9._:-]{1,253}$/u;
/** The rail's operation is a UUID (36); the runner adds ":<sessionId>" and the World's door takes 128 at most. */
const OPERATION_ID = /^[A-Za-z0-9._:-]{1,64}$/u;
const KEEP_MAX = 2000;
/** The person's Pocket room and the directory are read at most this many pages (64 records each). */
const PAGES_MAX = 256;
/** How long one archive call waits before answering where the tidy stands (the Desk's route waits the same). */
const JOIN_WAIT_MS = 15_000;
/** Session-times reads (64 sessions each) in flight at once. */
const TIMES_READS_AT_ONCE = 4;

const readRoomsRef = makeFunctionReference<
  "query",
  { worldKey: string; cursor?: string; entry?: { roomId: string; sessionId: string }; roomVocabulary: typeof WORKROOM_WIRE },
  unknown
>("boards:readRooms");
const readSessionChildrenRef = makeFunctionReference<
  "query",
  { worldKey: string; parent: { roomId: string } | { sessionId: string }; cursor?: string; roomVocabulary: typeof WORKROOM_WIRE },
  unknown
>("boards:readSessionChildren");
const readSessionTimesRef = makeFunctionReference<
  "query",
  { worldKey: string; sessionIds: ReadonlyArray<string> },
  unknown
>("boards:readSessionTimes");
const retireOwnSessionRef = makeFunctionReference<
  "action",
  { worldKey: string; roomId: string; sessionId: string; operationId: string },
  unknown
>("worldSessionRetirementRail:retireOwnSession");

const decodeChildrenPage = Schema.decodeUnknownOption(WorldSessionChildrenPage);
const decodeSessionTimes = Schema.decodeUnknownOption(WorldSessionTimes);
const decodeRoomEntry = Schema.decodeUnknownOption(WorldRoomEntry);

const record = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;

/** Whether the World holds a session's whole history: every participant kept, none unresolved (sessionTidyLive.ts). */
export const historyInWorld = (session: WorldSessionProjection): boolean => {
  const kept = new Set((session.transcripts ?? []).map((row) => row.threadId));
  return (
    (session.unresolvedExecutions ?? []).length === 0 &&
    (session.participants ?? []).every((row) => kept.has(row.threadId))
  );
};

interface SessionTime {
  readonly activeAt: number;
  readonly openedByReader: boolean;
  readonly retired: boolean;
}

/** The World's time for each of these sessions (boards:readSessionTimes, 64 a read, TIMES_READS_AT_ONCE at once). */
async function readTimes(client: ConvexClient, worldKey: string, sessionIds: ReadonlyArray<string>): Promise<Map<string, SessionTime>> {
  const ids = [...new Set(sessionIds)];
  const batches: Array<ReadonlyArray<string>> = [];
  for (let at = 0; at < ids.length; at += WORLD_SESSION_TIMES_MAX) batches.push(ids.slice(at, at + WORLD_SESSION_TIMES_MAX));
  const times = new Map<string, SessionTime>();
  let next = 0;
  const worker = async () => {
    while (next < batches.length) {
      const asked = batches[next++]!;
      const decoded = decodeSessionTimes(await client.query(readSessionTimesRef, { worldKey, sessionIds: asked }));
      if (Option.isNone(decoded) || decoded.value.worldKey !== worldKey) throw new Error("session-times-read-mismatch");
      const wanted = new Set(asked);
      for (const row of decoded.value.sessions)
        if (wanted.has(row.sessionId))
          times.set(row.sessionId, { activeAt: row.activeAt, openedByReader: row.openedByReader === true, retired: row.retired === true });
    }
  };
  await Promise.all(Array.from({ length: Math.min(TIMES_READS_AT_ONCE, batches.length) }, worker));
  return times;
}

/** The room a session sits in, from its own first page of children; undefined when the World does not hold it. */
async function roomOfSession(client: ConvexClient, worldKey: string, sessionId: string): Promise<string | undefined> {
  const children = record(
    await client.query(readSessionChildrenRef, { worldKey, parent: { sessionId }, roomVocabulary: WORKROOM_WIRE }),
  );
  const parent = record(children?.["parent"]);
  const named = parent?.["roomId"];
  return children?.["worldKey"] === worldKey && parent?.["sessionId"] === sessionId && typeof named === "string" && ROOM_ID.test(named)
    ? named
    : undefined;
}

/** Sessions this window has seen retired, for as long as the page lives (retirement is final in the World). */
const retiredSessions = new RetiredSessions();
/** This window's running tidies, one per World and operation. */
const tidyRuns = new SessionTidyRuns();
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * The tidy's dependencies in the browser host: the World's person doors under the window's own sign-in. Every session
 * read names its room, so a candidate's entry is read by (room, session) without another lookup.
 */
export function makeWebHostTidyDependencies(client: ConvexClient, worldKey: string, actorId: string): SessionTidyDependencies {
  const roomOf = new Map<string, string>();
  return {
    // No conversation is held in the browser: the Desk's own-session stage has nothing to judge here.
    readThreads: async () => [],
    readPocketSessions: async (asked) => {
      if (asked !== worldKey) throw new Error("world-mismatch");
      const roomId = `R_pocket_${actorId}`;
      const rows: WorldSessionProjection[] = [];
      let cursor: string | undefined;
      for (let page = 0; ; page++) {
        if (page >= PAGES_MAX) throw new Error("pocket-room-too-long");
        const decoded = decodeChildrenPage(
          await client.query(readSessionChildrenRef, { worldKey, parent: { roomId }, ...(cursor === undefined ? {} : { cursor }), roomVocabulary: WORKROOM_WIRE }),
        );
        if (Option.isNone(decoded) || decoded.value.worldKey !== worldKey) throw new Error("pocket-room-read-mismatch");
        const read = decoded.value;
        // No such room (or not this reader's to see): nothing of Pocket to tidy.
        if (read.parentId === null) return [];
        if (read.parentId !== roomId) throw new Error("pocket-room-read-mismatch");
        rows.push(...read.sessions.filter((row) => row.roomId === roomId));
        if (read.continueCursor === null) break;
        cursor = read.continueCursor;
      }
      for (const row of rows) roomOf.set(row.sessionId, roomId);
      const times = await readTimes(client, worldKey, rows.map((row) => row.sessionId));
      return rows.map((row): TidyPocketSession => ({
        sessionId: row.sessionId,
        label: row.label,
        roomId,
        open: row.lifecycle !== "retired" && row.retirement == null,
        retired: row.lifecycle === "retired" || times.get(row.sessionId)?.retired === true,
        activeAt: times.get(row.sessionId)?.activeAt ?? null,
        openedByReader: times.get(row.sessionId)?.openedByReader ?? false,
        historyInWorld: historyInWorld(row),
      }));
    },
    // Every other room in scope, from the directory the rail itself reads (boards:readRooms, a page at a time): the
    // rooms by kind, then every session of those rooms with the World's time for it. A Pocket room is read above; an
    // archived room is not on the rail.
    readWorldSessions: async ({ worldKey: asked, inScope, heldHere }) => {
      if (asked !== worldKey) throw new Error("world-mismatch");
      const rooms = new Map<string, { label: string; kind: "general" | "workroom"; workroomKey: string | null }>();
      const sessions = new Map<string, { sessionId: string; roomId: string; label: string; lifecycle?: string | undefined; parentId: string }>();
      let cursor: string | undefined;
      for (let page = 0; ; page++) {
        if (page >= PAGES_MAX) throw new Error("world-directory-too-long");
        const answer = await client.query(readRoomsRef, { worldKey, ...(cursor === undefined ? {} : { cursor }), roomVocabulary: WORKROOM_WIRE });
        const shaped = personRoomPageAsIndexPage(answer, { worldKey, actorId, cursor });
        if (shaped === null) throw new Error("world-directory-read-mismatch");
        for (const room of shaped.rooms) {
          if (room.archived === true || room.roomId.startsWith("R_pocket_")) continue;
          const kind = room.kind === "workroom" ? "workroom" : "general";
          const workroomKey = kind === "workroom" ? (room.workroomKey ?? null) : null;
          if (inScope({ roomId: room.roomId, kind, workroomKey })) rooms.set(room.roomId, { label: room.label, kind, workroomKey });
        }
        for (const session of shaped.sessions)
          sessions.set(session.sessionId, { sessionId: session.sessionId, roomId: session.roomId, label: session.label, lifecycle: session.lifecycle, parentId: session.parentId });
        if (shaped.continueCursor === null || shaped.continueCursor === undefined) break;
        cursor = shaped.continueCursor;
      }
      // W-9b736ece (Tester run 20261022T083531Z-seat-test-3afb3f55): the sessions under the person's own Pocket
      // conversations, which Pocket's room read (above) does not list, so each goes before the conversation over it.
      const pocketRoomId = `R_pocket_${actorId}`;
      if (inScope({ roomId: pocketRoomId, kind: "general", workroomKey: null })) rooms.set(pocketRoomId, { label: "Pocket", kind: "general", workroomKey: null });
      const here = [...sessions.values()].filter(
        (session) => rooms.has(session.roomId) && (session.roomId !== pocketRoomId || session.parentId.startsWith("S_")),
      );
      for (const session of here) roomOf.set(session.sessionId, session.roomId);
      const times = await readTimes(client, worldKey, here.filter((session) => !heldHere.has(session.sessionId)).map((session) => session.sessionId));
      return here.map((session): TidyWorldSession => {
        const room = rooms.get(session.roomId)!;
        const time = times.get(session.sessionId);
        return {
          sessionId: session.sessionId,
          label: session.label,
          roomId: session.roomId,
          roomLabel: room.label,
          roomKind: room.kind,
          activeAt: time?.activeAt ?? null,
          // Left out of the times read: not live, or not the reader's to see.
          openedByReader: time?.openedByReader ?? false,
          retired: time?.retired === true || session.lifecycle === "retired",
          // A hand's parent is a session (W-9b736ece); a root's is its room.
          ...(session.parentId.startsWith("S_") ? { parentId: session.parentId } : {}),
        };
      });
    },
    readEntry: async ({ worldKey: asked, sessionId }) => {
      if (asked !== worldKey) throw new Error("world-mismatch");
      const roomId = roomOf.get(sessionId) ?? (await roomOfSession(client, worldKey, sessionId));
      if (roomId === undefined) throw new Error("session-not-found");
      const decoded = decodeRoomEntry(await client.query(readRoomsRef, { worldKey, entry: { roomId, sessionId }, roomVocabulary: WORKROOM_WIRE }));
      if (Option.isNone(decoded) || decoded.value.worldKey !== worldKey || decoded.value.session.sessionId !== sessionId)
        throw new Error("entry-read-mismatch");
      return decoded.value;
    },
    readRetired: async ({ worldKey: asked, sessionIds }) => {
      if (asked !== worldKey) throw new Error("world-mismatch");
      const times = await readTimes(client, worldKey, sessionIds);
      return sessionIds.filter((sessionId) => times.get(sessionId)?.retired === true);
    },
    // The person's own retirement door (W-9a3e7442): reserve, the World-held final snapshots and the retirement in one
    // World action, under the tidy's operation for this session; the World's own words when it keeps the session.
    retire: async ({ worldKey: asked, roomId, sessionId, operationId }): Promise<TidyRetirementOutcome> => {
      if (asked !== worldKey) return { ok: false, reason: "world-mismatch" };
      let answer: Record<string, unknown> | undefined;
      try {
        answer = record(await client.action(retireOwnSessionRef, { worldKey, roomId, sessionId, operationId }));
      } catch (error) {
        const words = refusalOfWorldError(error);
        return { ok: false, reason: words.body.kind === "not_signed_in" ? "world-access-refused" : "world-refused", detail: words.body.reason };
      }
      if (answer?.["worldKey"] !== worldKey || answer?.["sessionId"] !== sessionId || typeof answer["retired"] !== "boolean")
        return { ok: false, reason: "retirement-unresolved" };
      if (answer["retired"] !== true)
        return { ok: false, reason: "world-refused", detail: typeof answer["why"] === "string" ? answer["why"] : "the World did not retire it" };
      return { ok: true };
    },
    retirementsAtOnce: 1,
    retired: retiredSessions,
  };
}

type TidyAnswer = { readonly status: number; readonly body: Record<string, unknown> };
const refuse = (status: number, reason: string): TidyAnswer => ({ status, body: { ok: false, reason } });
const INVALID = refuse(400, "invalid-request");

const stringList = (value: unknown, max: number): ReadonlyArray<string> | undefined =>
  Array.isArray(value) && value.length <= max && value.every((item) => typeof item === "string" && item.length >= 1 && item.length <= 256)
    ? (value as ReadonlyArray<string>)
    : undefined;

/** The tidy request as the Desk's route takes it (sessionTidyHttp.ts Tidy), or undefined when malformed. */
export function tidyRequestOf(body: Record<string, unknown>, now: number):
  | { readonly mode: "preview" | "archive"; readonly request: TidyRequest; readonly operationId?: string; readonly count?: number }
  | { readonly mode: "changes" | "pins" }
  | undefined {
  const allowed = new Set(["mode", "worldKey", "days", "roomId", "roomIds", "general", "workroomKey", "keep", "operationId", "count"]);
  if (Object.keys(body).some((key) => !allowed.has(key))) return undefined;
  const mode = body["mode"];
  const worldKey = body["worldKey"];
  if (typeof worldKey !== "string" || worldKey.length < 1 || worldKey.length > 256) return undefined;
  if (mode === "changes" || mode === "pins") return { mode };
  if (mode !== "preview" && mode !== "archive") return undefined;
  const days = body["days"];
  if (typeof days !== "number" || !validTidyDays(days)) return undefined;
  const roomId = body["roomId"];
  if (roomId !== undefined && (typeof roomId !== "string" || !ROOM_ID.test(roomId))) return undefined;
  const roomIds = body["roomIds"] === undefined ? undefined : stringList(body["roomIds"], KEEP_MAX);
  if (body["roomIds"] !== undefined && roomIds === undefined) return undefined;
  const general = body["general"];
  if (general !== undefined && typeof general !== "boolean") return undefined;
  const workroomKey = body["workroomKey"];
  if (workroomKey !== undefined && (typeof workroomKey !== "string" || workroomKey.length < 1 || workroomKey.length > 256)) return undefined;
  const keep = body["keep"] === undefined ? undefined : stringList(body["keep"], KEEP_MAX);
  if (body["keep"] !== undefined && keep === undefined) return undefined;
  const request: TidyRequest = {
    worldKey,
    days,
    roomId: roomId ?? null,
    roomIds: roomIds ?? [],
    general: general === true,
    ...(workroomKey === undefined ? {} : { workroomKey }),
    keep: keep ?? [],
    now,
  };
  if (mode === "preview") return { mode, request };
  const operationId = body["operationId"];
  const count = body["count"];
  if (typeof operationId !== "string" || !OPERATION_ID.test(operationId)) return undefined;
  if (typeof count !== "number" || !Number.isInteger(count) || count < 1) return undefined;
  return { mode, request, operationId, count };
}

/** A refusal's words: the World's own for a Convex error, else the error's message, never a bare code when it has words. */
const wordsOf = (error: unknown): string => {
  const words = refusalOfWorldError(error).body.reason;
  return words.length > 0 ? words : "tidy-unresolved";
};

/**
 * The Desk's tidy route in the browser host. Null for the modes that are the Mac's (changes, pins), so fetchPrimary
 * says it needs the Mac as before; 400 invalid-request for a malformed body; 409 with the reason in words when the tidy
 * refused (the count changed since the preview, the World could not be read); else the Desk route's own answers.
 */
export async function sessionTidyDoor(
  client: ConvexClient,
  actorId: string,
  body: Record<string, unknown>,
  now: () => number = Date.now,
): Promise<TidyAnswer | null> {
  const parsed = tidyRequestOf(body, now());
  if (parsed === undefined) return INVALID;
  if (!("request" in parsed)) return null;
  const deps = makeWebHostTidyDependencies(client, parsed.request.worldKey, actorId);
  if (parsed.mode === "preview") {
    try {
      const plan = await planSessionTidy(parsed.request, deps);
      return { status: 200, body: { ok: true, plan, words: describeTidyPlan(plan) } };
    } catch (error) {
      return refuse(409, wordsOf(error));
    }
  }
  try {
    const run = await tidyRuns.join(
      { ...parsed.request, operationId: parsed.operationId!, expectCount: parsed.count },
      deps,
      JOIN_WAIT_MS,
      sleep,
    );
    return { status: 200, body: { ok: true, run } };
  } catch (error) {
    return refuse(409, wordsOf(error));
  }
}
